import { useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import withMobileView from '@/hoc/withMobileView';
import { roundRating, isServer } from '@/helpers';
import pinStyles from './Markers.module.scss';
import ReactDOM from 'react-dom/client';
import Card from '@/components/elements/Card/Card';
import { placeService } from '@/services';
import React from 'react';

import { SearchPlace } from '@/types/api/services/search';
type PinStyles = {
  [key: string]: string | undefined;
};

const getPinSrc = (active: boolean, rating: number | false): string => {
  if (active) {
    return rating ? '/images/map-pin-selected.svg' : '/images/map-pin-selected-empty.svg';
  }

  return rating ? '/images/map-pin.svg' : '/images/map-pin-empty.svg';
};

const createCircle = (center: [number, number], radius: number, options = { steps: 64, units: 'kilometers' }) => {
  const { steps = 64, units = 'kilometers' } = options;
  const coords: [number, number][] = [];

  // conversion factors to meters
  const factors: Record<string, number> = {
    meters: 1,
    kilometers: 1000,
    miles: 1609.344,
    feet: 0.3048,
  };

  const R = 6371008.8; // mean Earth radius in meters
  const radiusInMeters = radius * (factors[units] || 1);

  const [lng, lat] = center.map((c) => (c * Math.PI) / 180); // radians

  for (let i = 0; i <= steps; i++) {
    const bearing = (((i * 360) / steps) * Math.PI) / 180;

    const lat2 = Math.asin(
      Math.sin(lat) * Math.cos(radiusInMeters / R) + Math.cos(lat) * Math.sin(radiusInMeters / R) * Math.cos(bearing),
    );
    const lng2 =
      lng +
      Math.atan2(
        Math.sin(bearing) * Math.sin(radiusInMeters / R) * Math.cos(lat),
        Math.cos(radiusInMeters / R) - Math.sin(lat) * Math.sin(lat2),
      );

    coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }

  // pick a random point from the circle (not including the duplicate last point)
  const randomIndex = Math.floor(Math.random() * steps);
  const randomPoint = coords[randomIndex];

  // line from center to that point
  const line = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [center, randomPoint],
    },
    properties: {
      label: `${radius} ${units}`,
    },
  };

  // label point
  const label = {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: randomPoint,
    },
    properties: {
      label: `radius = ${radius} ${units}`,
    },
  };

  // circle polygon
  const circle = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
    properties: {},
  };

  return {
    type: 'FeatureCollection',
    features: [circle, line, label],
  };
};

const createPlacePopup = (
  map: any,
  maptilersdk: any,
  place: SearchPlace,
  isActive: boolean,
  getPlaceDetails: (place: SearchPlace) => React.ReactNode,
  pinStyles: PinStyles,
) => {
  const popupContainer = document.createElement('div');
  popupContainer.className = 'w-full';
  const reactRoot = ReactDOM.createRoot(popupContainer);
  reactRoot.render(React.createElement(Card, null, getPlaceDetails(place)));

  const popup = new maptilersdk.Popup({
    className: `overflow-hidden drop-shadow-sm rounded-md ${pinStyles.placePopup}`,
    offset: [0, isActive ? -70 : -50],
    focusAfterOpen: false,
    anchor: 'bottom',
    maxWidth: '300px',
  }).setDOMContent(popupContainer);

  popup.on('open', () => {
    const popupElement = popup.getElement();
    if (!popupElement) {
      return;
    }

    const mapContainer = map.getContainer();
    const mapBounds = mapContainer.getBoundingClientRect();
    const popupBounds = popupElement.getBoundingClientRect();

    let offsetX = 0;
    let offsetY = 0;
    const padding = 10;

    // Check if popup is out of view
    if (popupBounds.left < mapBounds.left + padding) {
      offsetX = popupBounds.left - (mapBounds.left + padding);
    } else if (popupBounds.right > mapBounds.right - padding) {
      offsetX = popupBounds.right - (mapBounds.right - padding);
    }
    if (popupBounds.top < mapBounds.top + padding) {
      offsetY = popupBounds.top - (mapBounds.top + padding);
    } else if (popupBounds.bottom > mapBounds.bottom - padding) {
      offsetY = popupBounds.bottom - (mapBounds.bottom - padding);
    }
    if (offsetX !== 0 || offsetY !== 0) {
      map.panBy([offsetX, offsetY], {
        duration: 500, // add a smooth animation
      });
    }
  });

  return { popup, reactRoot, container: popupContainer };
};

interface MarkersProps {
  map: any;
  places: SearchPlace[];
  carouselIndex: number;
  setCarouselIndex: (index: number) => void;
  getPlaceDetails: (place: SearchPlace) => React.ReactNode;
  showPopup?: boolean;
  // Injected by withMobileView HOC
  isMobileView?: boolean;
  // Injected by connect (Redux)
  placeHover?: string;
  search?: {
    debugLocation?: any[];
  };
}

function Markers(props: MarkersProps) {
  const {
    map,
    places,
    carouselIndex,
    setCarouselIndex,
    getPlaceDetails,
    isMobileView, // <-- Provided by withMobileView HOC
    placeHover, // <--- Redux state: ID of currently active marker
    search,
    showPopup = true,
  } = props;

  // Access MapTiler SDK objects from the window or via the map instance
  const maptilersdk = window.maptilersdk || map?.getMapLibre();

  const markersRef = useRef([]);
  const prevPlaceIdsRef = useRef([]);
  const debugLayersRef = useRef([]);

  const createMarkerElement = (place: SearchPlace, isActive: boolean, rating: number | false, pinStyles: PinStyles) => {
    const iconSize = isActive ? [60, 67] : [40, 40];
    const el = document.createElement('span');
    const markerClassIsActiveName = isActive ? pinStyles?.placePinActive || 'place-pin-active' : '';
    const zIndexOffset = isActive ? '100' : '3';

    el.className = `place-pin ${markerClassIsActiveName}`;
    el.style.width = `${iconSize[0]}px`;
    el.style.height = `${iconSize[1]}px`;
    el.style.cursor = 'pointer';
    el.style.position = 'absolute'; // Required for MapTiler custom elements
    el.style.zIndex = zIndexOffset; // Z-index handled by popup z-index listener

    const img = document.createElement('img');
    img.className = pinStyles.placePinImage;
    img.src = getPinSrc(isActive, rating);
    img.alt = place.about?.name || 'Place Pin';
    el.appendChild(img);

    if (rating) {
      const ratingEl = document.createElement('span');
      ratingEl.className = pinStyles.placePinRating;
      ratingEl.textContent = String(rating);
      el.appendChild(ratingEl);
    }

    return el;
  };

  const displayPlaceDetails = useCallback(
    (place: SearchPlace, key: number) => {
      if (!isServer) {
        placeService.storeSerpImpressions([place.id]);

        if (isMobileView && setCarouselIndex) {
          setCarouselIndex(key);
        }
      }
    },
    [isMobileView, setCarouselIndex],
  );

  interface MarkerItem {
    marker: any;
    markerElement: HTMLElement | null;
    clickHandler: ((event: MouseEvent) => void) | null;
    reactRoot?: ReactDOM.Root;
    container?: HTMLElement;
    placeId: string | number;
    rating: number | false;
    isActive: boolean;
    popup?: any;
  }

  const removeMarker = useCallback((item: MarkerItem) => {
    if (item.markerElement && item.clickHandler) {
      // remove click event listener on mobile
      item.markerElement.removeEventListener('click', item.clickHandler);
    }
    if (item.popup && item.popup.isOpen()) {
      // remove popup if it's open
      item.popup.remove();
    }
    if (item.reactRoot) {
      setTimeout(() => {
        item.reactRoot!.unmount();
      }, 0);
    }
    if (item.marker && typeof item.marker.remove === 'function') {
      item.marker.remove();
    }
  }, []);

  const cleanupMarkers = useCallback(() => {
    markersRef.current.forEach((item: MarkerItem) => removeMarker(item));
    markersRef.current = [];

    // Clean up debug layers
    debugLayersRef.current.forEach((layerId) => {
      if (map && map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map && map.getSource(layerId)) {
        map.removeSource(layerId);
      }
    });
    debugLayersRef.current = [];
  }, [map, markersRef, removeMarker]);

  const addMarkersToMap = useCallback(async () => {
    if (!map || !maptilersdk) {
      cleanupMarkers();
      return;
    }

    const currentPlaceIds = places.map((place: SearchPlace) => place.id);
    const prevPlaceIds = prevPlaceIdsRef.current; // Use previous IDs only for desktop view

    const placesToRemove = markersRef.current.filter((item) => !currentPlaceIds.includes(item.placeId));
    const placesToAdd = places.filter((place: SearchPlace) => !prevPlaceIds.includes(place.id));

    placesToRemove.forEach((item) => {
      removeMarker(item);
      markersRef.current = markersRef.current.filter((markerItem) => markerItem.placeId !== item.placeId);
    });

    placesToAdd.forEach((place: SearchPlace) => {
      if (place.contact?.position) {
        const placeIndex = places.findIndex((p) => p.id === place.id);
        const { lat, lon } = place.contact.position;
        const coordinates = [parseFloat(String(lon)), parseFloat(String(lat))];
        let isActive = false; // Markers are inactive by default when added. The useEffect will activate them.

        if (isMobileView) {
          // Mobile: ONLY carouselIndex dictates visual active marker
          isActive = carouselIndex === placeIndex;
        }

        const rating = place?.rating?.score ? roundRating(place.rating.score) : false;

        // Create the marker element using the helper function
        const el = createMarkerElement(place, isActive, rating, pinStyles);

        const popupDetails = showPopup
          ? createPlacePopup(map, maptilersdk, place, isActive, getPlaceDetails, pinStyles)
          : null;
        const popup = popupDetails?.popup;
        const reactRoot = popupDetails?.reactRoot;
        const popupContainer = popupDetails?.container;

        const clickHandler = () => {
          displayPlaceDetails(place, placeIndex);
        };
        el.addEventListener('click', clickHandler);

        const marker = new maptilersdk.Marker({
          element: el,
          anchor: 'bottom',
        }).setLngLat(coordinates);

        if (popup) marker.setPopup(popup);

        marker.addTo(map);

        markersRef.current.push({
          marker: marker,
          markerElement: el, // Store the element reference
          clickHandler: clickHandler, // Store the handler reference
          reactRoot: reactRoot,
          container: popupContainer,
          placeId: place.id, // Store place ID
          rating: rating, // Store place rating
          isActive: isActive, // Store initial active state
        });
      }
    });

    const clearMap = async (map) => {
      const style = map.getStyle();

      if (style && style.layers) {
        // remove layers in reverse order (safer, avoids dependency issues)
        [...style.layers].reverse().forEach((layer) => {
          if (/debug-location/.test(layer.id) && map.getLayer(layer.id)) {
            map.removeLayer(layer.id);
          }
        });
      }

      if (style && style.sources) {
        Object.keys(style.sources).forEach((sourceId) => {
          if (/debug-location/.test(sourceId) && map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        });
      }
    };

    // --- Add Debug Locations (Polygons, Circles, Rectangles) ---
    if (search?.debugLocation?.length > 0) {
      const hasRectangle = search.debugLocation.flat().some((item) => item.type === 'rectangle');
      if (!hasRectangle) {
        await clearMap(map);
      }

      search.debugLocation?.forEach((locationGroup, groupIndex) => {
        locationGroup.forEach((loc, locIndex) => {
          const id = `debug-location-${groupIndex}-${locIndex}`;
          const color = loc?.must_not ? 'red' : 'green';

          if (loc.type === 'polygon') {
            const polygonCoordinates = [
              loc.coordinates[0].map((coord) => [parseFloat(coord[1]), parseFloat(coord[0])]),
            ];

            if (!map.getSource(id)) {
              // Only add source if it doesn't exist
              map.addSource(id, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: polygonCoordinates,
                  },
                },
              });
            }
            if (!map.getLayer(id)) {
              // Only add layer if it doesn't exist
              map.addLayer({
                id: id,
                type: 'fill',
                source: id,
                paint: {
                  'fill-color': color,
                  'fill-opacity': 0.2,
                  'fill-outline-color': color,
                },
              });
            }
          } else if (loc.type === 'geo_distance') {
            const circle = createCircle([parseFloat(loc.position[1]), parseFloat(loc.position[0])], loc.distance);
            if (!map.getSource(id)) {
              map.addSource(id, {
                type: 'geojson',
                data: circle,
              });
            }

            // fill layer for the polygon
            if (!map.getLayer(`${id}-fill`)) {
              map.addLayer({
                id: `${id}-fill`,
                type: 'fill',
                source: id,
                filter: ['==', ['geometry-type'], 'Polygon'],
                paint: {
                  'fill-color': color,
                  'fill-opacity': 0.2,
                },
              });
            }

            // outline for the polygon
            if (!map.getLayer(`${id}-outline`)) {
              map.addLayer({
                id: `${id}-outline`,
                type: 'line',
                source: id,
                filter: ['==', ['geometry-type'], 'Polygon'],
                paint: {
                  'line-color': color,
                  'line-width': 1,
                },
              });
            }

            // radius line (center -> random point on circle)
            if (!map.getLayer(`${id}-radius`)) {
              map.addLayer({
                id: `${id}-radius`,
                type: 'line',
                source: id,
                filter: ['==', ['geometry-type'], 'LineString'],
                paint: {
                  'line-color': color,
                  'line-width': 2,
                  'line-dasharray': [2, 2], // optional: dashed
                },
              });
            }

            // label at the end of the radius line
            if (!map.getLayer(`${id}-label`)) {
              map.addLayer({
                id: `${id}-label`,
                type: 'symbol',
                source: id,
                filter: ['==', ['geometry-type'], 'Point'],
                layout: {
                  'text-field': ['get', 'label'],
                  'text-size': 12,
                  'text-offset': [0, 1.2],
                  'text-anchor': 'top',
                  'symbol-placement': 'point',
                  'text-allow-overlap': true, // optional, if you really want it visible
                },
                paint: {
                  'text-halo-width': 1,
                  'text-halo-color': '#ffffff',
                },
              });
            }

            // debugLayersRef.current.push(id);
            debugLayersRef.current.push(`${id}-fill`, `${id}-outline`, `${id}-radius`, `${id}-label`);
          } else if (loc.type === 'rectangle') {
            // For rectangles, create a GeoJSON Polygon from bounds.
            // `loc.position` is `[[southLat, westLng], [northLat, eastLng]]` (Leaflet-style bounds).
            // Convert to GeoJSON Polygon expected `[[westLng, southLat], [eastLng, southLat], [eastLng, northLat], [westLng, northLat], [westLng, southLat]]`
            const southWest = loc.position[0]; // [southLat, westLng]
            const northEast = loc.position[1]; // [northLat, eastLng]

            const rectangleCoordinates = [
              [parseFloat(southWest[1]), parseFloat(southWest[0])], // westLng, southLat
              [parseFloat(northEast[1]), parseFloat(southWest[0])], // eastLng, southLat
              [parseFloat(northEast[1]), parseFloat(northEast[0])], // eastLng, northLat
              [parseFloat(southWest[1]), parseFloat(northEast[0])], // westLng, northLat
              [parseFloat(southWest[1]), parseFloat(southWest[0])], // westLng, southLat (close the loop)
            ];

            if (!map.getSource(id)) {
              map.addSource(id, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: [rectangleCoordinates],
                  },
                },
              });
            }

            if (!map.getLayer(id)) {
              map.addLayer({
                id: id,
                type: 'fill',
                source: id,
                paint: {
                  'fill-color': color,
                  'fill-opacity': 0.2,
                  'fill-outline-color': color,
                },
              });
            }
            debugLayersRef.current.push(id);
          }
        });
      });
    }

    prevPlaceIdsRef.current = currentPlaceIds;

    return () => {
      cleanupMarkers();
    };
  }, [map, maptilersdk, isMobileView, getPlaceDetails, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Manage popup visibility based on placeHover or carouselIndex
  useEffect(() => {
    const currentMarkersById = new Map();
    markersRef.current.forEach((markerItem) => {
      currentMarkersById.set(markerItem.placeId, markerItem);
    });

    places.forEach((place: SearchPlace, placeIndex: number) => {
      const item = currentMarkersById.get(place.id);
      if (item) {
        let isActive: boolean;
        if (isMobileView) {
          isActive = carouselIndex === placeIndex;
        } else {
          isActive =
            placeHover !== undefined &&
            place.id !== undefined &&
            parseInt(placeHover, 10) === parseInt(String(place.id), 10);
        }

        item.isActive = isActive;

        const zIndexOffset = isActive ? '100' : '3';

        // Update marker element class and image
        if (item.marker && item.marker.getElement) {
          const el = item.marker.getElement();

          if (el) {
            el.classList.remove(pinStyles.placePinActive || 'place-pin-active');
            if (isActive) {
              el.classList.add(pinStyles.placePinActive || 'place-pin-active');
            }

            const iconSize = isActive ? [60, 67] : [40, 40];
            el.style.width = `${iconSize[0]}px`;
            el.style.height = `${iconSize[1]}px`;
            el.style.zIndex = zIndexOffset;

            const img = el.querySelector('img');
            if (img) {
              img.src = getPinSrc(isActive, item.rating);
            }

            if (isMobileView) {
              el.removeEventListener('click', item.clickHandler);
              const clickHandler = () => {
                displayPlaceDetails({ id: item.placeId }, placeIndex);
              };
              el.addEventListener('click', clickHandler);
              item.clickHandler = clickHandler;

              // Ensure popup is closed/removed for mobile
              if (item.popup && item.popup.isOpen()) {
                item.popup.remove();
              }
              // Disconnect popup from marker so it doesn't open on click
              if (item.marker && item.marker.getPopup()) {
                item.marker.setPopup(null);
              }
            } else {
              if (item.markerElement && item.clickHandler) {
                // remove if mobile click handler was attached for Desktop
                item.markerElement.removeEventListener('click', item.clickHandler);
                item.clickHandler = null;
              }

              // Ensure popup is set on the marker (MapTiler SDK handles open/close on marker click)
              if (showPopup && item.marker && item.popup && !item.marker.getPopup()) {
                item.marker.setPopup(item.popup);
              }
            }
          }
        }
      }
    });
  }, [isMobileView, carouselIndex, placeHover, places, displayPlaceDetails]);

  useEffect(() => {
    addMarkersToMap();
  }, [addMarkersToMap]);

  useEffect(() => {
    return () => {
      cleanupMarkers(); // This ensures all markers and debug layers are removed when component unmounts
    };
  }, [cleanupMarkers]);

  return null;
}

function mapStateToProps(state) {
  const { placeHover } = state.place;
  const { searchV2: search } = state;
  return { placeHover, search };
}

const MarkersConnected = withMobileView(connect(mapStateToProps)(Markers));

export { MarkersConnected as MapTilerMarkers };
